import {
    CloneActivityData,
    DeliverySession,
} from '../../../interfaces/activity';

// Defines what gets cloned and how
export const CLONE_CONFIG = {
    // Fields that are copied as-is from the original activity
    directCopy: [
        'activity_audience',
        'activity_modality',
        'activity_name',
        'activity_type',
        'catalog_item_versioned_id',
        'class_size',
        'course_catalog_item_id',
        'course_days',
        'course_name',
        'customer_support_manager',
        'customer_support_manager_email',
        'delivery_address_1',
        'delivery_address_2',
        'delivery_city',
        'delivery_country',
        'delivery_facility_type',
        'delivery_geo',
        'delivery_language',
        'delivery_postal_code',
        'delivery_region',
        'delivery_state',
        'delivery_timezone',
        'facility_size',
        'lms_type',
        'partner_initiative',
        'program',
        'provider',
        'room',
        'topic_name',
        'v_ilt_type',
        'waitlist_enabled',
    ] as const,

    // Fields that are set to specific values on clone
    overrides: {
        // Always set cloned activities to Hold status
        activity_status: 'Hold' as const,
        // ownership should not be null
        additional_owners: [],
        // instructors should not be null
        instructors: [],
        // A new LMS ID will be generated
        lms_id: null,
    },

    // Invoice fields that get reset to default values
    invoiceResets: {
        aws_sor: null,
        batch_id: null,
        billed_amount: 0,
        billing_status: 'Draft' as const,
        invoice_reference: null,
        line_items: [],
        mast_invoice_number: null,
        number_of_participants: 0,
        ofa_invoice_number: null,
        ofa_invoice_url: null,
        s3_path: null,
        sfdc_opportunity_id: null,
    },

    // Customer fields that get cleared on clone
    customerResets: [
        'accounts_payable_email',
        'email_sent',
        'most_recent_email_date',
        'notify_customer_date',
        'number_of_students_attended',
        'number_of_students_committed',
        'pricing_model',
        'sfdc_contract_request_id',
        'sfdc_line_item_id',
        'sfdc_opportunity_id',
        'sfdc_opportunity_name',
        'sfdc_scheduling_request_id',
        'training_briefing_motivations_for_using_aws',
        'training_briefing_special_needs_for_training_delivery',
        'training_briefing_what_the_customer_does',
        'training_briefing_where_are_they_in_their_cloud_journey',
    ] as const,
};

// Helper functions for cloning
export const cloneHelpers = {
    // Copy configured fields from original activity
    buildBaseFields: (formValues: any): Partial<CloneActivityData> => {
        return CLONE_CONFIG.directCopy.reduce(
            (acc, field) => ({
                ...acc,
                [field]: formValues[field],
            }),
            {} as Partial<CloneActivityData>,
        );
    },

    // Filter and convert delivery sessions
    processDeliverySessions: (
        sessionItems: Array<any>,
        getDeliverySession: (item: any, timezone: string) => DeliverySession,
        timezone: string,
    ): DeliverySession[] => {
        return sessionItems
            .filter((item) => item.startTime && item.endTime && item.dateString)
            .map((item) => getDeliverySession(item, timezone));
    },

    // Filter and reset invoice fields
    processInvoices: (
        invoices: Array<any>,
        selectedIndexes: number[],
    ): Array<any> => {
        return invoices
            .filter((_, index) => selectedIndexes.includes(index))
            .map((invoice) => ({
                ...invoice,
                ...CLONE_CONFIG.invoiceResets,
            }));
    },

    // Filter and clear customer fields
    processCustomers: (
        customers: Array<any>,
        selectedIndexes: number[],
    ): Array<any> => {
        const resetFields = CLONE_CONFIG.customerResets.reduce(
            (acc, field) => ({ ...acc, [field]: undefined }),
            {} as Record<string, undefined>,
        );

        return customers
            .filter((_, index) => selectedIndexes.includes(index))
            .map((customer) => ({
                ...customer,
                ...resetFields,
                revenues: [],
            }));
    },
};
